import {useEffect, useState, useCallback} from 'react'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {KTIcon, toAbsoluteUrl} from '../../../helpers'
import {useLayout} from '../../core'
import {Header} from './Header'
import {Navbar} from './Navbar'
import Pusher from 'pusher-js'
import {useAuth} from '../../../../app/modules/auth'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import useNotificationStore from '../../../store/notificationStore'
import axios from 'axios'
import {NotificationToastContainer, useNotificationToast} from '../../../partials/notifications'
import {
  PusherChatMessage,
  PusherStatusUpdate,
  ChatNotification,
  StatusNotification,
} from '../../../../types/notificationTypes'
import useNotificationSound from '../../../../hooks/useNotificationSound'
import {ensureBenefitDetail} from '../../../../utils/ensureBenefitDetail'
import {
  getChatNotifications,
  getStatusNotifications,
} from '../../../../app/services/notification.service'

export function HeaderWrapper() {
  const {config, classes} = useLayout()
  const [notificationCount, setNotificationCount] = useState<number>(0)
  const [showNotificationMenu, setShowNotificationMenu] = useState(false)
  const [activeNotificationTab, setActiveNotificationTab] = useState<'chat' | 'status'>('chat')

  // Header dropdown notification state (fetched from API)
  const [headerChatNotifications, setHeaderChatNotifications] = useState<ChatNotification[]>([])
  const [headerStatusNotifications, setHeaderStatusNotifications] = useState<StatusNotification[]>(
    []
  )
  const [headerLoading, setHeaderLoading] = useState(false)

  const {currentUser} = useAuth()
  const providerCode = currentUser?.providerCode
  const addNotification = useNotificationStore((state) => state.addNotification)
  const addChatNotificationFromPusher = useNotificationStore(
    (state) => state.addChatNotificationFromPusher
  )
  const addStatusNotificationFromPusher = useNotificationStore(
    (state) => state.addStatusNotificationFromPusher
  )
  const notifications = useNotificationStore((state) => state.notifications)
  const chatUnreadCount = useNotificationStore((state) => state.chatUnreadCount)
  const statusUnreadCount = useNotificationStore((state) => state.statusUnreadCount)
  const setChatUnreadCount = useNotificationStore((state) => state.setChatNotifications)
  const setStatusUnreadCount = useNotificationStore((state) => state.setStatusNotifications)
  const navigate = useNavigate()
  const user = useAuth()
  const {auth} = useAuth()

  // Custom toast hook for notification toasts
  const {toasts, addToast, removeToast} = useNotificationToast()

  // Notification sound hook (uses debounce to prevent multiple sounds)
  const {playNotification} = useNotificationSound()

  // Calculate total unread count
  const totalUnreadCount = chatUnreadCount + statusUnreadCount + notificationCount

  console.log('total unread ', chatUnreadCount);
  

  // Fetch header notifications from API
  const fetchHeaderNotifications = useCallback(async () => {
    setHeaderLoading(true)
    try {
      const [chatResponse, statusResponse] = await Promise.all([
        getChatNotifications({limit: 5, onlyUnread: true}),
        getStatusNotifications({limit: 5, onlyUnread: true}),
      ])

      if (chatResponse.isSuccess && chatResponse.data) {
        setHeaderChatNotifications(chatResponse.data.items)
      }
      if (statusResponse.isSuccess && statusResponse.data) {
        setHeaderStatusNotifications(statusResponse.data.items)
      }
    } catch (error) {
      console.error('Error fetching header notifications:', error)
    } finally {
      setHeaderLoading(false)
    }
  }, [])

  const fetchClaimDetails = async (claimNumber: string) => {
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/GetByClaimNumber/${claimNumber}`
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })
      console.log('Claim details fetched:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching claim details:', error)
      return null
    }
  }

  const handleClaimAction = async (claimNumber: string) => {
    console.log('Test Button : ', claimNumber)
    const claimDetails = await fetchClaimDetails(claimNumber)
    console.log('Claim details:', claimDetails)
    if (claimDetails) {
      if (!ensureBenefitDetail(claimDetails)) return

      console.log('Claim details:', claimDetails)
      navigate('/inpatient', {state: {claimDetails}})
    }
  }

  useEffect(() => {
    if (!providerCode) {
      console.log('[Pusher] No providerCode available, skipping Pusher setup')
      return
    }

    console.log('[Pusher] Initializing Pusher connection...')
    console.log('[Pusher] Provider Code:', providerCode)
    console.log('[Pusher] Pusher Key:', process.env.REACT_APP_API_PUSHER_KEY)
    console.log('[Pusher] Pusher Cluster:', process.env.REACT_APP_API_PUSHER_CLUSTER)

    const pusher = new Pusher(`${process.env.REACT_APP_API_PUSHER_KEY!}`, {
      cluster: process.env.REACT_APP_API_PUSHER_CLUSTER! || 'ap1',
      forceTLS: true,
    })

    // Log connection state changes
    pusher.connection.bind('state_change', (states: {previous: string; current: string}) => {
      console.log('[Pusher] Connection state changed:', states.previous, '->', states.current)
    })

    pusher.connection.bind('connected', () => {
      console.log('[Pusher] ✅ Connected successfully!')
      console.log('[Pusher] Socket ID:', pusher.connection.socket_id)
    })

    pusher.connection.bind('error', (err: any) => {
      console.error('[Pusher] ❌ Connection error:', err)
    })

    // Subscribe to existing file channel (backward compatibility)
    const fileChannelName = `file-channel-${providerCode}`
    console.log('[Pusher] Subscribing to file channel:', fileChannelName)
    const fileChannel = pusher.subscribe(fileChannelName)

    fileChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[Pusher] ✅ Successfully subscribed to:', fileChannelName)
    })

    fileChannel.bind('pusher:subscription_error', (err: any) => {
      console.error('[Pusher] ❌ Failed to subscribe to:', fileChannelName, err)
    })

    const fileEventName = `get-${providerCode}`
    console.log('[Pusher] Binding to file event:', fileEventName)
    fileChannel.bind(fileEventName, (data: any) => {
      console.log('[Pusher] 📩 Received file notification:', data)
      if (data && data.message) {
        const {message} = data
        const {Status, ClaimNumber, UpdatedBy, UpdatedAt} = message

        if (Status === 'INVALID' || Status === 'Request Document' || Status === 'VALID') {
          setNotificationCount((prev) => prev + 1)

          // Add notification to Zustand store with isRead set to false
          addNotification({
            ClaimNumber,
            Status,
            UpdatedBy,
            UpdatedAt: new Date(UpdatedAt).toLocaleString(),
            isRead: false,
          })

          // Show toast notification with details
          const toastMessage =
            Status === 'VALID'
              ? `Claim ${ClaimNumber} has been approved by ${UpdatedBy} on ${new Date(
                  UpdatedAt
                ).toLocaleString()}`
              : Status === 'Request Document'
              ? `Claim ${ClaimNumber} has been added for review by ${UpdatedBy} on ${new Date(
                  UpdatedAt
                ).toLocaleString()}`
              : `Claim ${ClaimNumber} has been rejected by ${UpdatedBy} on ${new Date(
                  UpdatedAt
                ).toLocaleString()}`

          toast(toastMessage, {type: Status === 'INVALID' ? 'error' : 'info'})

          // Play notification sound
          playNotification()
        }
      }
    })

    // Subscribe to Chat Message channel
    const chatChannelName = `MessageChannel-${providerCode}`
    console.log('[Pusher] Subscribing to chat channel:', chatChannelName)
    const chatChannel = pusher.subscribe(chatChannelName)

    chatChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[Pusher] ✅ Successfully subscribed to:', chatChannelName)
    })

    chatChannel.bind('pusher:subscription_error', (err: any) => {
      console.error('[Pusher] ❌ Failed to subscribe to:', chatChannelName, err)
    })

    console.log('[Pusher] Binding to chat event: new-message')
    chatChannel.bind('new-message', (data: PusherChatMessage) => {
      console.log('[Pusher] 📩 Received chat notification (raw):', JSON.stringify(data, null, 2))
      console.log('[Pusher] Chat data.message:', data?.message)
      console.log('[Pusher] Chat data.message?.claimNumber:', data?.message?.claimNumber)

      if (data && data.message && data.message.claimNumber) {
        console.log('[Pusher] ✅ Processing chat notification for claim:', data.message.claimNumber)

        // Add to store - pass the message payload
        addChatNotificationFromPusher(data.message)

        // Show custom toast
        addToast('chat', data.message.claimNumber)

        // Play notification sound (debounced)
        playNotification()

        console.log('[Pusher] ✅ Chat notification processed successfully')
      } else {
        console.warn('[Pusher] ⚠️ Chat notification data structure mismatch:', {
          hasData: !!data,
          hasMessage: !!data?.message,
          hasClaimNumber: !!data?.message?.claimNumber,
        })
      }
    })

    // Subscribe to Status Update channel
    const statusChannelName = `provider-update-claim-${providerCode}`
    console.log('[Pusher] Subscribing to status channel:', statusChannelName)
    const statusChannel = pusher.subscribe(statusChannelName)

    statusChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[Pusher] ✅ Successfully subscribed to:', statusChannelName)
    })

    statusChannel.bind('pusher:subscription_error', (err: any) => {
      console.error('[Pusher] ❌ Failed to subscribe to:', statusChannelName, err)
    })

    console.log('[Pusher] Binding to status event: status-updated')
    statusChannel.bind('status-updated', (data: PusherStatusUpdate) => {
      console.log('[Pusher] 📩 Received status notification (raw):', JSON.stringify(data, null, 2))
      console.log('[Pusher] Status data.message:', data?.message)
      console.log('[Pusher] Status data.message?.ClaimNumber:', data?.message?.ClaimNumber)

      if (data && data.message && data.message.ClaimNumber) {
        console.log(
          '[Pusher] ✅ Processing status notification for claim:',
          data.message.ClaimNumber
        )

        // Add to store - pass the message payload
        addStatusNotificationFromPusher(data.message)

        // Show custom toast
        addToast('status', data.message.ClaimNumber)

        // Play notification sound (debounced)
        playNotification()

        console.log('[Pusher] ✅ Status notification processed successfully')
      } else {
        console.warn('[Pusher] ⚠️ Status notification data structure mismatch:', {
          hasData: !!data,
          hasMessage: !!data?.message,
          hasClaimNumber: !!data?.message?.ClaimNumber,
        })
      }
    })

    // Log all bound channels
    console.log('[Pusher] All channels subscribed. Waiting for events...')

    // Cleanup function
    return () => {
      console.log('[Pusher] Cleaning up Pusher connections...')
      fileChannel.unbind_all()
      chatChannel.unbind_all()
      statusChannel.unbind_all()
      pusher.unsubscribe(fileChannelName)
      pusher.unsubscribe(chatChannelName)
      pusher.unsubscribe(statusChannelName)
      pusher.disconnect()
      console.log('[Pusher] Disconnected')
    }
  }, [providerCode])

  if (!config.app?.header?.display) {
    return null
  }

  return (
    <div id='kt_app_header' className='app-header'>
      {/* Custom Toast Container for Chat/Status notifications */}
      <NotificationToastContainer toasts={toasts} onRemoveToast={removeToast} />

      <div
        id='kt_app_header_container'
        className={clsx(
          'app-container flex-lg-grow-1',
          classes.headerContainer.join(' '),
          config.app?.header?.default?.containerClass
        )}
      >
        {config.app.sidebar?.display && (
          <>
            <div
              className='d-flex align-items-center d-lg-none ms-n2 me-2'
              title='Show sidebar menu'
            >
              <div
                className='btn btn-icon btn-active-color-primary w-35px h-35px'
                id='kt_app_sidebar_mobile_toggle'
              >
                <KTIcon iconName='abstract-14' className=' fs-1' />
              </div>
              <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0'>
                <Link to='/dashboard' className='d-lg-none'>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/default-small.svg')}
                    className='h-30px'
                  />
                </Link>
              </div>
            </div>
          </>
        )}

        {!(config.layoutType === 'dark-sidebar' || config.layoutType === 'light-sidebar') && (
          <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0 me-lg-15'>
            <Link to='/dashboard'>
              {config.layoutType !== 'dark-header' ? (
                <img
                  alt='Logo'
                  src={toAbsoluteUrl('/media/logos/default.svg')}
                  className='h-20px h-lg-30px app-sidebar-logo-default'
                />
              ) : (
                <>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/default-dark.svg')}
                    className='h-20px h-lg-30px app-sidebar-logo-default theme-light-show'
                  />
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/default-small-dark.svg')}
                    className='h-20px h-lg-30px app-sidebar-logo-default theme-dark-show'
                  />
                </>
              )}
            </Link>
          </div>
        )}

        <div
          id='kt_app_header_wrapper'
          className='d-flex align-items-stretch justify-content-between flex-lg-grow-1'
        >
          {config.app.header.default?.content === 'menu' &&
            config.app.header.default.menu?.display && (
              <div
                className='app-header-menu app-header-mobile-drawer align-items-stretch'
                data-kt-drawer='true'
                data-kt-drawer-name='app-header-menu'
                data-kt-drawer-activate='{default: true, lg: false}'
                data-kt-drawer-overlay='true'
                data-kt-drawer-width='225px'
                data-kt-drawer-direction='end'
                data-kt-drawer-toggle='#kt_app_header_menu_toggle'
                data-kt-swapper='true'
                data-kt-swapper-mode="{default: 'append', lg: 'prepend'}"
                data-kt-swapper-parent="{default: '#kt_app_body', lg: '#kt_app_header_wrapper'}"
              >
                <Header />
              </div>
            )}

          <div className='app-navbar flex-shrink-0'>
            {/* Notification Bell */}
            <div className='app-navbar-item ms-1 ms-md-3 position-relative'>
              <div
                className='btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-30px h-30px w-md-40px h-md-40px position-relative cursor-pointer'
                onClick={() => {
                  const newState = !showNotificationMenu
                  setShowNotificationMenu(newState)
                  if (newState) {
                    fetchHeaderNotifications()
                    setNotificationCount(0)
                  }
                }}
              >
                <KTIcon iconName='notification' className='fs-2 fs-lg-1' />
                {totalUnreadCount > 0 && (
                  <span
                    className='position-absolute badge bg-danger'
                    style={{
                      top: '1px',
                      right: '1px',
                      padding: '0.25em 0.5em',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      color: '#FFFFFF',
                      borderRadius: '10px',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </span>
                )}
              </div>

              {/* Notification Dropdown Menu */}
              {showNotificationMenu && (
                <>
                  {/* Backdrop to close menu when clicking outside */}
                  <div
                    className='position-fixed top-0 start-0 w-100 h-100'
                    style={{zIndex: 100}}
                    onClick={() => setShowNotificationMenu(false)}
                  />
                  <div
                    className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px show position-absolute'
                    style={{
                      zIndex: 101,
                      top: '100%',
                      right: 0,
                      marginTop: '10px',
                      boxShadow: '0 0 50px 0 rgba(82, 63, 105, 0.15)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div
                      className='d-flex flex-column bgi-no-repeat rounded-top'
                      style={{backgroundColor: '#1e1e2d'}}
                    >
                      <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
                        Notifications{' '}
                        <span className='fs-8 opacity-75 ps-3'>{totalUnreadCount} Latest Update</span>
                      </h3>

                      {/* Tabs */}
                      <ul className='nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-bold px-9'>
                        <li className='nav-item'>
                          <button
                            className={`nav-link text-white opacity-75 opacity-state-100 pb-4 border-0 bg-transparent ${
                              activeNotificationTab === 'chat' ? 'active' : ''
                            }`}
                            onClick={() => setActiveNotificationTab('chat')}
                            style={{
                              borderBottom:
                                activeNotificationTab === 'chat' ? '2px solid #3699ff' : 'none',
                            }}
                          >
                            Chat
                            {chatUnreadCount > 0 && (
                              <span className='badge badge-sm bg-primary ms-2'>
                                {chatUnreadCount}
                              </span>
                            )}
                          </button>
                        </li>
                        <li className='nav-item'>
                          <button
                            className={`nav-link text-white opacity-75 opacity-state-100 pb-4 border-0 bg-transparent ${
                              activeNotificationTab === 'status' ? 'active' : ''
                            }`}
                            onClick={() => setActiveNotificationTab('status')}
                            style={{
                              borderBottom:
                                activeNotificationTab === 'status' ? '2px solid #8950fc' : 'none',
                            }}
                          >
                            Status
                            {statusUnreadCount > 0 && (
                              <span className='badge badge-sm bg-warning ms-2'>
                                {statusUnreadCount}
                              </span>
                            )}
                          </button>
                        </li>
                      </ul>
                    </div>

                    {/* Tab Content */}
                    <div className='tab-content' style={{padding: '10px'}}>
                      <div className='scroll-y mh-325px'>
                        {/* Chat Tab */}
                        {activeNotificationTab === 'chat' && (
                          <>
                            {headerLoading ? (
                              <div className='d-flex flex-center py-5'>
                                <span className='spinner-border spinner-border-sm text-primary' />
                              </div>
                            ) : headerChatNotifications.length === 0 ? (
                              <div className='d-flex flex-column align-items-center py-8'>
                                <KTIcon
                                  iconName='message-text-2'
                                  className='fs-3x text-gray-300 mb-3'
                                />
                                <span className='text-gray-500 fw-semibold'>No chat messages</span>
                              </div>
                            ) : (
                              headerChatNotifications.map((notification, index) => (
                                <div
                                  key={`chat-${index}`}
                                  className={`d-flex gap-3 p-3 rounded mb-2 cursor-pointer ${
                                    !notification.isOpen ? 'bg-light-primary' : ''
                                  }`}
                                  onClick={async () => {
                                    setShowNotificationMenu(false)
                                    await handleClaimAction(notification.claimNumber)
                                  }}
                                  style={{
                                    border: !notification.isOpen
                                      ? '1px solid #dbeafe'
                                      : '1px solid #f1f5f9',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <div className='symbol symbol-45px flex-shrink-0'>
                                    <span className='symbol-label bg-primary'>
                                      <KTIcon
                                        iconName='message-text-2'
                                        className='fs-2 text-white'
                                      />
                                    </span>
                                  </div>
                                  <div className='flex-grow-1 min-w-0'>
                                    <div className='d-flex align-items-center justify-content-between mb-1'>
                                      <span className='fw-bold text-dark fs-6'>
                                        {notification.claimNumber}
                                      </span>
                                      {/* {!notification.isOpen && (
                                        <span className='badge badge-primary badge-sm'>New</span>
                                      )} */}
                                    </div>
                                    <p
                                      className='text-gray-600 fs-7 mb-1 text-truncate'
                                      style={{maxWidth: '200px'}}
                                    >
                                      {notification.remark || 'New message received'}
                                    </p>
                                    <span className='text-gray-400 fs-8'>
                                      {notification.insurerName}
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </>
                        )}

                        {/* Status Tab */}
                        {activeNotificationTab === 'status' && (
                          <>
                            {headerLoading ? (
                              <div className='d-flex flex-center py-5'>
                                <span className='spinner-border spinner-border-sm text-warning' />
                              </div>
                            ) : headerStatusNotifications.length === 0 ? (
                              <div className='d-flex flex-column align-items-center py-8'>
                                <KTIcon
                                  iconName='notification-status'
                                  className='fs-3x text-gray-300 mb-3'
                                />
                                <span className='text-gray-500 fw-semibold'>No status updates</span>
                              </div>
                            ) : (
                              headerStatusNotifications.map((notification, index) => (
                                <div
                                  key={`status-${index}`}
                                  className={`d-flex gap-3 p-3 rounded mb-2 cursor-pointer ${
                                    !notification.isOpen ? 'bg-light-warning' : ''
                                  }`}
                                  onClick={async () => {
                                    setShowNotificationMenu(false)
                                    await handleClaimAction(notification.claimNumber)
                                  }}
                                  style={{
                                    border: !notification.isOpen
                                      ? '1px solid #fef08a'
                                      : '1px solid #f1f5f9',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <div className='symbol symbol-45px flex-shrink-0'>
                                    <span className='symbol-label bg-warning'>
                                      <KTIcon
                                        iconName='notification-status'
                                        className='fs-2 text-white'
                                      />
                                    </span>
                                  </div>
                                  <div className='flex-grow-1 min-w-0'>
                                    <div className='d-flex align-items-center justify-content-between mb-1'>
                                      <span className='fw-bold text-dark fs-6'>
                                        {notification.claimNumber}
                                      </span>
                                      {/* {!notification.isOpen && (
                                        <span className='badge badge-warning badge-sm'>New</span>
                                      )} */}
                                    </div>
                                    <p
                                      className='text-gray-600 fs-7 mb-1 text-truncate'
                                      style={{maxWidth: '200px'}}
                                    >
                                      {notification.remark || 'Status updated'}
                                    </p>
                                    <span className='text-gray-400 fs-8'>
                                      {notification.providerName}
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* View All Link */}
                    <div className='py-3 text-center border-top'>
                      <span
                        className='btn btn-color-gray-600 btn-active-color-primary cursor-pointer'
                        onClick={() => {
                          setShowNotificationMenu(false)
                          navigate('/notifications')
                        }}
                      >
                        View All <KTIcon iconName='arrow-right' className='fs-5' />
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* New Chat Button */}
            {/* <div className='app-navbar-item ms-1 ms-md-3'>
              <Link to='/apps/chat' className='btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary'>
                <KTIcon iconName='chat' className='fs-2 fs-lg-1' />
              </Link>
            </div> */}

            <Navbar />
          </div>
        </div>
      </div>
    </div>
  )
}
